// docs/22 O4 — the first named foe never dies on the board: when the sim drops it, the scene plays
// its getaway instead of the fall. Six small effects, each ≤ ~1.6 s at 1× and within the docs/19
// legibility rules: the motes are a few board pixels, nothing darkens more than the unit's own
// cell, and everything is drawn on the board camera's effects layer so it zooms with the board.
import Phaser from 'phaser';
import type { Escape } from '../../core/story/genesis';
import type { UnitSprite } from './UnitSprite';

export interface EscapeHost {
  scene: Phaser.Scene;
  /** Adds an effect object to the board's effects layer. */
  add(obj: Phaser.GameObjects.GameObject): void;
  cell: number;
  /** Board width in cells (Steed rides off the east edge). */
  boardW: number;
  speed: number;
  reduced: boolean;
}

/** Total length of each getaway at 1× (ms). */
export const ESCAPE_MS: Record<Escape, number> = { Smoke: 1100, Water: 1300, Burrow: 1400, Steed: 1500, Wings: 1500, Shadow: 1400 };

const rnd = (lo: number, hi: number) => lo + Math.random() * (hi - lo);

/** One square "pixel" mote: grows/drifts/fades by tween, then destroys itself. */
function mote(h: EscapeHost, x: number, y: number, size: number, colour: number, alpha: number, to: { x: number; y: number; scale?: number; alpha?: number }, ms: number, delay = 0, ease = 'Quad.easeOut'): void {
  const r = h.scene.add.rectangle(Math.round(x), Math.round(y), size, size, colour, alpha);
  h.add(r);
  // move for the whole life, fade over its second half so the burst reads before it thins
  h.scene.tweens.add({ targets: r, x: to.x, y: to.y, scale: to.scale ?? 1, duration: ms, delay, ease, onComplete: () => r.destroy() });
  h.scene.tweens.add({ targets: r, alpha: to.alpha ?? 0, duration: ms * 0.55, delay: delay + ms * 0.45, ease: 'Quad.easeIn' });
}

/** A thin ring at the unit's feet (water splash), flattened like the ground it lies on. */
function ring(h: EscapeHost, x: number, y: number, colour: number, ms: number, delay: number, to: number): void {
  const g = h.scene.add.graphics({ x, y });
  g.lineStyle(1, colour, 0.95);
  g.strokeEllipse(0, 0, 8, 3);
  g.setScale(1);
  h.add(g);
  h.scene.tweens.add({ targets: g, scaleX: to, scaleY: to, alpha: 0, duration: ms, delay, ease: 'Quad.easeOut', onComplete: () => g.destroy() });
}

/**
 * Plays the getaway on `u` and resolves when it is over (the unit is left invisible; the next
 * board drops it). Reduced motion: a short fade, no motes.
 */
export function playEscape(h: EscapeHost, u: UnitSprite, kind: Escape): Promise<void> {
  const k = 1 / Math.max(1, h.speed);
  const total = Math.round(ESCAPE_MS[kind] * k);
  const s = h.scene, c = h.cell;
  const fx = u.x, fy = u.y;                   // pivot: bottom centre of the cell
  const cy = fy - c / 2;                      // body centre
  if (h.reduced) {
    s.tweens.add({ targets: u, alpha: 0, duration: Math.round(240 * k) });
    return new Promise((r) => s.time.delayedCall(Math.round(260 * k), r));
  }
  const t = (ms: number) => Math.round(ms * k);
  switch (kind) {
    case 'Smoke': {
      // a burst of grey puffs, the body gone inside it
      const greys = [0xa9afb8, 0xd3d7dc, 0x7d838c, 0xeceef1];
      for (let i = 0; i < 26; i++) {
        const a = rnd(0, Math.PI * 2), d = rnd(4, c * 0.8);
        mote(h, fx + rnd(-4, 4), cy + rnd(-4, 6), Math.round(rnd(3, 5)), greys[i % greys.length]!, 1,
          { x: fx + Math.cos(a) * d, y: cy + Math.sin(a) * d * 0.7 - rnd(2, 10), scale: rnd(1.6, 2.6) }, t(rnd(700, 1000)), t(rnd(0, 140)));
      }
      s.tweens.add({ targets: u, alpha: 0, duration: t(160), delay: t(120) });
      break;
    }
    case 'Water': {
      // splash ring + droplets, then the body sinks under
      ring(h, fx, fy - 1, 0xbfe6ff, t(700), 0, 3.2);
      ring(h, fx, fy - 1, 0x7fbfe8, t(700), t(260), 2.4);
      for (let i = 0; i < 10; i++) {
        const dx = rnd(-c * 0.45, c * 0.45);
        mote(h, fx + dx * 0.3, fy - 2, 2, i % 2 ? 0xd8f1ff : 0x8cc8ee, 1, { x: fx + dx, y: fy - rnd(6, 14), alpha: 0 }, t(rnd(380, 560)), t(rnd(0, 120)), 'Quad.easeOut');
      }
      s.tweens.add({ targets: u.bodies, y: `+=${Math.round(c * 0.85)}`, duration: t(900), delay: t(200), ease: 'Quad.easeIn' });
      s.tweens.add({ targets: u, alpha: 0, duration: t(700), delay: t(400) });
      break;
    }
    case 'Burrow': {
      // a shudder, a spray of dirt, and down into the ground
      s.tweens.add({ targets: u, x: fx + 1, duration: t(45), yoyo: true, repeat: 4 });
      const dirt = [0x9a7040, 0xc39a5e, 0x7a5530, 0xe0bf86];
      for (let i = 0; i < 22; i++) {
        const dx = rnd(-c * 0.7, c * 0.7), up = rnd(6, 14), ms = rnd(520, 720);
        const r = s.add.rectangle(fx + rnd(-3, 3), fy - 2, i % 3 ? 2 : 3, i % 3 ? 2 : 3, dirt[i % dirt.length]!, 1);
        h.add(r);
        // up then down: a little arc
        s.tweens.chain({ targets: r, delay: t(300 + rnd(0, 260)), tweens: [
          { x: fx + dx * 0.6, y: fy - 2 - up, duration: t(ms * 0.45), ease: 'Quad.easeOut' },
          { x: fx + dx, y: fy + rnd(-2, 1), alpha: 0.2, duration: t(ms * 0.55), ease: 'Quad.easeIn' },
        ], onComplete: () => r.destroy() });
      }
      s.tweens.add({ targets: u.bodies, y: `+=${c}`, duration: t(800), delay: t(380), ease: 'Quad.easeIn' });
      s.tweens.add({ targets: u, alpha: 0, duration: t(600), delay: t(600) });
      break;
    }
    case 'Steed': {
      // a dash to the east edge, kicking up dust, fading as it leaves the board
      u.setFacing(1);
      const edge = h.boardW * c + c * 0.5;
      const dist = Math.max(c, edge - fx);
      const ms = t(Math.min(1300, 500 + dist * 7));
      s.tweens.add({ targets: u, x: edge, duration: ms, ease: 'Quad.easeIn' });
      s.tweens.add({ targets: u, alpha: 0, duration: t(260), delay: Math.max(0, ms - t(260)) });
      const n = Math.min(10, Math.max(4, Math.round(dist / c)));
      for (let i = 0; i < n; i++) {
        const p = i / n, x = fx + (edge - fx) * p * p;   // matches the ease-in of the dash
        mote(h, x, fy - 1, 4, i % 2 ? 0xd9c49e : 0xb39a76, 0.95, { x: x - rnd(4, 9), y: fy - rnd(4, 9), scale: 2 }, t(560), Math.round(ms * p * 0.9));
        mote(h, x + 3, fy - 2, 3, 0xece0c4, 0.9, { x: x - rnd(2, 6), y: fy - rnd(6, 11), scale: 1.6 }, t(480), Math.round(ms * p * 0.9) + t(40));
      }
      break;
    }
    case 'Wings': {
      // up and away, a flutter of dark feathers left behind
      s.tweens.add({ targets: u, y: fy - c * 3, scale: 0.7, duration: t(1100), ease: 'Quad.easeIn' });
      s.tweens.add({ targets: u, alpha: 0, duration: t(420), delay: t(700) });
      const dark = [0x2b2440, 0x6a5a8c, 0x1c1828, 0x9886b8];
      for (let i = 0; i < 18; i++) {
        const sx = fx + rnd(-c * 0.4, c * 0.4), sy = cy + rnd(-6, 4);
        const r = s.add.rectangle(sx, sy, 2, 3, dark[i % dark.length]!, 1);
        h.add(r);
        const sway = rnd(4, 9) * (i % 2 ? 1 : -1);
        s.tweens.chain({ targets: r, delay: t(rnd(80, 600)), tweens: [
          { x: sx + sway, y: sy + rnd(3, 6), angle: rnd(-60, 60), duration: t(380), ease: 'Sine.easeInOut' },
          { x: sx - sway * 0.5, y: sy + rnd(8, 13), alpha: 0, angle: rnd(-90, 90), duration: t(480), ease: 'Sine.easeInOut' },
        ], onComplete: () => r.destroy() });
      }
      break;
    }
    case 'Shadow': {
      // darken to a silhouette, flicker, and come apart in rising motes; a small pool at the feet
      u.sprite.setTint(0x1d1730).setTintMode(Phaser.TintModes.MULTIPLY);
      const pool = s.add.ellipse(fx, fy - 1, 6, 2, 0x0d0a16, 0.55);
      h.add(pool);
      s.tweens.add({ targets: pool, scaleX: 2.6, scaleY: 2.2, duration: t(500), ease: 'Quad.easeOut', yoyo: true, hold: t(300), onComplete: () => pool.destroy() });
      s.tweens.add({ targets: u, alpha: { from: 1, to: 0 }, duration: t(900), delay: t(260), ease: 'Stepped', easeParams: [6] });
      s.tweens.add({ targets: u.bodies, scaleY: 0.6, duration: t(900), delay: t(260), ease: 'Quad.easeIn' });
      const shade = [0x231b38, 0x6f5aa0, 0x14101f, 0xa48ad6];
      for (let i = 0; i < 20; i++) {
        mote(h, fx + rnd(-c * 0.35, c * 0.35), cy + rnd(-c * 0.35, c * 0.4), 2, shade[i % shade.length]!, 0.95,
          { x: fx + rnd(-c * 0.5, c * 0.5), y: cy - rnd(8, 18), alpha: 0 }, t(rnd(520, 760)), t(300 + rnd(0, 500)), 'Sine.easeOut');
      }
      break;
    }
  }
  return new Promise((r) => s.time.delayedCall(total, r));
}
